import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { BiCalendar, BiMenu, BiSearch } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { NotificationsBell } from '@/components/layout/NotificationsBell';
import { UserMenu } from '@/components/layout/UserMenu';
import { CALENDAR_ROLES } from '@/types/shared';
import './Topbar.css';

interface TopbarProps {
  onToggleNav: () => void;
}

export function Topbar({ onToggleNav }: TopbarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Client-side convenience gate mirroring `nav-items.ts`'s "a module a role can't read is
  // omitted entirely, never shown greyed-out" rule — CALENDAR_ROLES is the shared T103 RBAC
  // matrix's derived role list (Student Affairs Officer is the one zero-type role, T104 §6.3). The
  // real boundary is the server's 403 + the `/calendar` route's `RoleGuard`, this is UI-only.
  const canSeeCalendar = Boolean(user && CALENDAR_ROLES.includes(user.role));

  return (
    <header className="fa-topbar">
      <button
        type="button"
        className="fa-topbar__nav-toggle"
        aria-label={t('topbar.toggleNav')}
        onClick={onToggleNav}
      >
        <BiMenu aria-hidden="true" />
      </button>

      <div className="fa-topbar__brand">
        <img className="fa-topbar__logo" src="/academy-logo.jpeg" alt="" aria-hidden="true" />
        <span className="fa-topbar__name">{t('app.name')}</span>
      </div>

      <label className="fa-topbar__search">
        <span className="fa-visually-hidden">{t('topbar.searchPlaceholder')}</span>
        <BiSearch className="fa-topbar__search-icon" aria-hidden="true" />
        <input type="search" placeholder={t('topbar.searchPlaceholder')} />
      </label>

      <div className="fa-topbar__actions">
        <LanguageToggle />
        {canSeeCalendar && (
          <NavLink
            to="/calendar"
            className={({ isActive }) =>
              `fa-topbar__icon-btn${isActive ? ' fa-topbar__icon-btn--active' : ''}`
            }
            aria-label={t('calendar.topbarIcon')}
            title={t('calendar.topbarIcon')}
          >
            <BiCalendar aria-hidden="true" />
          </NavLink>
        )}
        <NotificationsBell />
        <UserMenu />
      </div>
    </header>
  );
}
